import {Injectable} from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanLoad,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment,
  UrlTree
} from '@angular/router';
import {Observable, throwError} from 'rxjs';
import {catchError, map, switchMap, take, tap} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {VetClinicService} from './vet-clinic.service';
import {ApiService} from './api.service';
import {UserService} from "./user.service";

/**
 * Guard chroniący autoryzowane strefy aplikacji (Dashboard).
 * Odpowiada za weryfikację statusu logowania użytkownika. Wdraża mechanizm "Auto-Login"
 * (odtwarzanie sesji) oraz aktywną walidację tokena po stronie serwera API.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanLoad, CanActivate {
  constructor(
    private authService: AuthService,
    private vetClinicService: VetClinicService,
    private router: Router,
    private apiService: ApiService,
    private userService: UserService
  ) {  }

  /**
   * Blokuje lub zezwala na asynchroniczne pobranie (Lazy Loading) chronionych modułów aplikacji.
   * W pierwszej kolejności weryfikuje lokalny stan uwierzytelnienia. W przypadku jego braku,
   * próbuje zrekonstruować sesję z pamięci przeglądarki (autoLogin). Jeśli lokalny stan
   * wskazuje na bycie zalogowanym, strażnik dodatkowo odpytuje backend w celu weryfikacji
   * ciągłej ważności tokena (Active Session Verification).
   *
   * @param route Konfiguracja ścieżki przeznaczonej do załadowania.
   * @param segments Segmenty adresu URL.
   * @returns Observable emitujący wartość logiczną (true w przypadku posiadania ważnej sesji).
   */
  canLoad(
    route: Route,
    segments: UrlSegment[]
  ): Observable<boolean> | Promise<boolean> | boolean {
    return this.authService.userIsAuthenticated.pipe(
      take(1),
      switchMap(isAuthenticated => {
        if (!isAuthenticated) {
          return this.authService.autoLogin();
        } else {
          // authenticated check token

          return this.apiService.post('/dashboard/user', {}).pipe(take(1),
            catchError(err => {
              if (err.status === 401) {
                this.router.navigate(['signin']).then(() => {
                  this.authService.logout();
                });
              }
              return throwError(err);
            }), map(response => {
              console.log('check - lead - ok');
              return true;
            }));
        }
      }),
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          this.router.navigate(['signin']);
        }
      })
    );
  }

  /**
   * Zabezpiecza przed fizycznym wyrenderowaniem (aktywacją) chronionego widoku.
   * Implementuje logikę walidacyjną (lokalny stan -> autoLogin -> weryfikacja API),
   * zabezpieczając nawigację wewnętrzną już po załadowaniu kodu modułu.
   *
   * @param route Migawka aktywowanej ścieżki.
   * @param state Migawka stanu routera.
   * @returns Observable emitujący wartość logiczną lub drzewo routingu.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.authService.userIsAuthenticated.pipe(
      take(1),
      switchMap(isAuthenticated => {
        console.log('canActivate',isAuthenticated);

        if (!isAuthenticated) {
          return this.authService.autoLogin();
        } else {
          // authenticated check token
          return this.apiService.post('/dashboard/user', {}).pipe(take(1),
            catchError(err => {
              if (err.status === 401) {
                this.router.navigate(['signin']).then(() => {
                  this.authService.logout();
                });
              }
              return throwError(err);
            }), map(response => {
              return true;
            }));
        }
      }),
      tap(isAuthenticated => {
        if (!isAuthenticated) {
          this.router.navigate(['signin']);
        }
      })
    );
  }

}
